import { useUpcomingCallsQuery } from './index'
import { secondsToMs } from '@thyme/libs/src/time/convert'
import { DateTime } from 'luxon'
import { onMounted, onUnmounted, ref, watch } from 'vue'
import type { ReminderReason, UpcomingCall } from './types'

// How often we re-check stored calls' start times against the current time.
const TICK_INTERVAL_SECONDS = 30

// How far ahead of a call's start we announce it.
export const REMINDER_LEAD_MINUTES = 5

const FIRST_ACTION_LISTENER_OPTIONS = { capture: true } as const

const FIRST_ACTION_STORAGE_KEY = 'call-reminder-first-action-day'

/**
 * Keyed on id & start time, so a rescheduled call is able to re-announce
 * @param call
 * @param reason why the card is being shown. Defaults to `upcoming`.
 */
export function buildAnnouncementKey(
  call: UpcomingCall,
  reason: ReminderReason = 'upcoming'
): string {
  switch (reason) {
    case 'missed': {
      if (call.isDateOnly)
        return `${call.communicationId}:missed:${call.dueDate}`
      return `${call.communicationId}:missed:${call.startTime.toISO()}`
    }
    case 'upcoming':
    default: {
      if (call.isDateOnly) return `${call.communicationId}:day:${call.dueDate}`
      return `${call.communicationId}:at:${call.startTime.toISO()}`
    }
  }
}

const DISMISSED_STORAGE_KEY = 'dismissed-call-reminders'

/**
 * Announcement keys the user has dismissed in this tab.
 */
export function readDismissedReminders(): Set<string> {
  try {
    const stored = sessionStorage.getItem(DISMISSED_STORAGE_KEY)
    const parsed: unknown = stored ? JSON.parse(stored) : []
    if (!Array.isArray(parsed)) return new Set()
    return new Set(
      parsed.filter((key): key is string => typeof key === 'string')
    )
  } catch {
    return new Set()
  }
}

/**
 * @param announcementKey the dismissed toast's id, i.e. `buildAnnouncementKey` output
 */
export function markReminderDismissed(announcementKey: string): void {
  const dismissed = readDismissedReminders()
  dismissed.add(announcementKey)
  try {
    sessionStorage.setItem(
      DISMISSED_STORAGE_KEY,
      JSON.stringify([...dismissed])
    )
  } catch (err) {
    // Storage full or blocked. The toast stays dismissed for this page load either way,
    // since `announced` already holds the key in memory.
    console.error(
      `Failed to add ${announcementKey} dismissed toast to session storage`,
      err
    )
  }
}

/**
 * The calendar day, 'yyyy-MM-dd', the user was last seen acting in this tab.
 */
export function readFirstActionDay(): string | null {
  try {
    return sessionStorage.getItem(FIRST_ACTION_STORAGE_KEY)
  } catch {
    return null
  }
}

/**
 * @param day the calendar day to record, 'yyyy-MM-dd'
 */
export function markFirstActionDay(day: string): void {
  try {
    sessionStorage.setItem(FIRST_ACTION_STORAGE_KEY, day)
  } catch (err) {
    // Storage full or blocked
    console.error(
      `Failed to add first user action of the day (${day}) to session storage`,
      err
    )
  }
}

/**
 * @param onCallsDue called with the calls that just came due, never with an empty array
 */
export function useUpcomingCallReminders(
  onCallsDue: (calls: UpcomingCall[]) => void
): void {
  const day = ref(DateTime.now())
  const { data: calls } = useUpcomingCallsQuery(day)
  const announced = new Set<string>()
  let tickHandle: ReturnType<typeof setInterval> | undefined

  // Date string vs boolean because users leave tabs open indefinitely.
  // In-memory copy of the stored session value, seeded on mount, so a blocked or full
  // sessionStorage degrades to "one click per document" instead of never opening the
  // gate at all.
  let firstActionDay: string | null = null

  /**
   * Announces any calls now within the lead time
   */
  function checkForDueCalls() {
    const now = DateTime.now()
    // Only reassign on a date change, so the query key stays stable within a day
    if (!now.hasSame(day.value, 'day')) day.value = now

    const dismissed = readDismissedReminders()

    const callsDue = (calls.value ?? []).filter((call) => {
      // Date-only calls handled by checkForDateOnlyCalls; triggered on first user click of day
      if (call.isDateOnly) return false

      const announcementKey = buildAnnouncementKey(call)
      if (dismissed.has(announcementKey)) return false
      if (announced.has(announcementKey)) return false
      const minutesUntilStart = call.startTime.diff(now).as('minutes')
      // Forward-looking only: a call whose start has passed is ignored (may need adjustment for all day calls in future)
      return (
        minutesUntilStart >= 0 && minutesUntilStart <= REMINDER_LEAD_MINUTES
      )
    })

    if (!callsDue.length) return

    for (const call of callsDue) announced.add(buildAnnouncementKey(call))
    onCallsDue(callsDue)
  }

  /**
   * Announces today's date-only calls, but only once the user has acted in the app today.
   *
   * Gated on firstActionDay rather than filtered by the caller, so both triggers can call
   * it blindly: whichever of (user action, data arrival) happens second is the one that
   * actually announces.
   */
  function checkForDateOnlyCalls() {
    // prevents date-only toast from firing before user has acted (clicked) today
    // if query loads before user is actually active
    if (firstActionDay !== DateTime.now().toFormat('yyyy-MM-dd')) return

    const dismissed = readDismissedReminders()

    const callsDue = (calls.value ?? []).filter((call) => {
      // Timed calls announce off their start time, in checkForDueCalls
      if (!call.isDateOnly) return false
      const announcementKey = buildAnnouncementKey(call)
      if (dismissed.has(announcementKey)) return false
      return !announced.has(announcementKey)
    })

    if (!callsDue.length) return

    for (const call of callsDue) announced.add(buildAnnouncementKey(call))
    onCallsDue(callsDue)
  }

  /**
   * Any click in the app counts as the day's first action.
   */
  function onUserAction() {
    const today = DateTime.now().toFormat('yyyy-MM-dd')
    if (firstActionDay === today) return
    firstActionDay = today
    markFirstActionDay(today)
    checkForDateOnlyCalls()
  }

  // Added to address checks as soon as data lands because data sometimes lands post
  // mount, if flags hadn't loaded yet. Also fires every time the day's calls that the
  // query returns changes (select result is memoized, so uses cache if no change
  // to calls on query rather than rerunning toUpcomingCall())
  watch(calls, () => {
    checkForDueCalls()
    // date-only added to cover if a click precedes data/flags loading
    checkForDateOnlyCalls()
  })

  onMounted(() => {
    // Before the checks below, so a reload or a tab opened from a toast picks up an
    // action already taken today rather than waiting for another click.
    firstActionDay = readFirstActionDay()
    // Check for timed calls on mount, so a call already inside the lead time isn't held up
    checkForDueCalls()
    checkForDateOnlyCalls()
    tickHandle = setInterval(
      checkForDueCalls,
      secondsToMs(TICK_INTERVAL_SECONDS)
    )
    // Registered for the life of the tab rather than just the first click, so a tab left
    // open overnight still hears the next day's first action.
    document.addEventListener(
      'click',
      onUserAction,
      FIRST_ACTION_LISTENER_OPTIONS
    )
  })

  onUnmounted(() => {
    clearInterval(tickHandle)
    document.removeEventListener(
      'click',
      onUserAction,
      FIRST_ACTION_LISTENER_OPTIONS
    )
  })
}
